import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline, ExpandMore } from "@mui/icons-material";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import FieldMappingSelector from "../../components/FieldMappingSelector";
import {
  DUPLICATE_HANDLING_OVERWRITE,
  DUPLICATE_HANDLING_SKIP,
  DUPLICATE_HANDLING_RESET,
  DUPLICATE_HANDLING_ALLOW_DUPLICATES,
} from "../../constants";

interface FieldMapping {
  email?: string[];
  phone?: string[];
  linkedinUrl?: string[];
  address?: string[];
  city?: string[];
  state?: string[];
  zip?: string[];
}

interface ListHeader {
  id: string;
  name: string;
  type?: string;
}

export default ({
  actionParameters,
  setActionParameters,
  getToken,
  setValidForm,
  companyId,
  headers = [],
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  getToken: Function;
  setValidForm: Function;
  companyId: string;
  headers?: ListHeader[];
}) => {
  const [sequences, setSequences] = useState([]);
  const [showFieldMappings, setShowFieldMappings] = useState(false);

  // Filter out action type headers
  const availableHeaders = headers.filter((h) => h.type !== "action");

  const loadData = async () => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/sequences`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setSequences(res.data.items);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setValidForm(actionParameters.sequenceId);
  }, [actionParameters]);

  const handleFieldMappingsChange = (mappings: FieldMapping) => {
    // Only store non-empty mappings
    const cleanMappings: FieldMapping = {};
    if (mappings.email?.length) cleanMappings.email = mappings.email;
    if (mappings.phone?.length) cleanMappings.phone = mappings.phone;
    if (mappings.linkedinUrl?.length) cleanMappings.linkedinUrl = mappings.linkedinUrl;
    if (mappings.address?.length) cleanMappings.address = mappings.address;
    if (mappings.city?.length) cleanMappings.city = mappings.city;
    if (mappings.state?.length) cleanMappings.state = mappings.state;
    if (mappings.zip?.length) cleanMappings.zip = mappings.zip;

    setActionParameters({
      ...actionParameters,
      fieldMappings: Object.keys(cleanMappings).length > 0 ? cleanMappings : undefined,
    });
  };

  return (
    <Box>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          Sequence
          <Tooltip title="Select the email/LinkedIn sequence to add matching rows to. Rows will be enrolled as new members of the sequence and will start receiving messages.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Sequence</InputLabel>
          <Select
            label="Sequence"
            value={actionParameters.sequenceId || ""}
            onChange={(e) => {
              setActionParameters({
                ...actionParameters,
                sequenceId: e.target.value,
              });
            }}
          >
            {sequences.map((sequence: any) => (
              <MenuItem key={sequence.id} value={sequence.id}>
                {sequence.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          Duplicate Handling
          <Tooltip title="What to do when a contact with the same email, LinkedIn URL, or phone already exists in the sequence">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <FormControl fullWidth>
          <InputLabel>When duplicate found</InputLabel>
          <Select
            label="When duplicate found"
            value={actionParameters.duplicateHandling || DUPLICATE_HANDLING_OVERWRITE}
            onChange={(e) => {
              setActionParameters({
                ...actionParameters,
                duplicateHandling: e.target.value,
              });
            }}
          >
            <MenuItem value={DUPLICATE_HANDLING_OVERWRITE}>
              Update existing member (keep sequence position)
            </MenuItem>
            <MenuItem value={DUPLICATE_HANDLING_SKIP}>
              Skip (don't modify existing member)
            </MenuItem>
            <MenuItem value={DUPLICATE_HANDLING_RESET}>
              Reset existing member to step 1
            </MenuItem>
            <MenuItem value={DUPLICATE_HANDLING_ALLOW_DUPLICATES}>
              Allow duplicates (add new instance)
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Advanced Section */}
      {availableHeaders.length > 0 && (
        <Accordion
          expanded={showFieldMappings}
          onChange={() => setShowFieldMappings(!showFieldMappings)}
          sx={{ mt: 2, boxShadow: "none", "&:before": { display: "none" } }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ px: 0, minHeight: "auto", "& .MuiAccordionSummary-content": { my: 1 } }}
          >
            <Typography variant="body2" color="text.secondary">
              Advanced
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Field Mapping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Customize which fields are used for contact info and address.
              Fields are tried in the order you select them.
            </Typography>
            <FieldMappingSelector
              fieldMappings={actionParameters.fieldMappings || {}}
              onChange={handleFieldMappingsChange}
              availableHeaders={availableHeaders}
              showHelp={false}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
